/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Common utils for subscriptions
 */
import _ from 'lodash';

import dateParser from 'lib/dateParser';
import { STRIPE_PAYMENT_STATUSES, ADDON_CODES, CHARGES_CODE } from 'app-constants/admin/subscriptions';
import { SHORT_DATE_FORMAT, YEAR_MONTH_DATE_FORMAT, EMPTY_VALUE } from '@veritable/common/constants';
/**
 * Renders plan name
 */
const renderPlanName = (planName?: string): string => {
	if (planName) {
		return `${planName} Plan`;
	}
	return EMPTY_VALUE;
};

interface IInvoiceAmountBreakdown {
	lineItems: { label: string; amount: number }[];
	total: number;
}

/**
 * Returns structured breakdown of invoice amount line items and total
 */
const getInvoiceAmountBreakdown = (fees: Subscriptions.FeesModel[], creditAmount?: number): IInvoiceAmountBreakdown => {
	const baseFee = _.find(fees, (fee) => fee.item?.type === 'subscription');
	if (!baseFee?.amount_cents) {
		return null;
	}

	const adjustedBaseFee = baseFee.amount_cents - (creditAmount ?? 0);
	const lineItems: { label: string; amount: number }[] = [
		{ label: `${_.capitalize(baseFee.plan_interval)} plan`, amount: adjustedBaseFee / 100 },
	];

	const overageFees = _.filter(fees, (fee) => fee.item?.type === 'charge' && fee.item?.code !== ADDON_CODES.payments);
	const overageCharge = _.sumBy(overageFees, (fee) => fee.amount_cents ?? 0);
	if (overageCharge > 0) {
		lineItems.push({ label: 'Overage charges', amount: overageCharge / 100 });
	}

	const addOnFees = _.filter(fees, (fee) => fee.item?.code === ADDON_CODES.payments);
	const addOnCharge = _.get(addOnFees, [0, 'amount_cents'], 0);
	if (addOnCharge > 0) {
		lineItems.push({ label: `${_.first(addOnFees)?.item?.name} Add-On`, amount: addOnCharge / 100 });
	}

	return { lineItems, total: _.sumBy(lineItems, 'amount') };
};

/**
 * Checks if payment is failed
 */
const isFailedPayment = (invoice?: Subscriptions.InvoiceModel): boolean => {
	return invoice?.payment_status === STRIPE_PAYMENT_STATUSES.failure.value;
};

/**
 * check if payment should be retried
 */
const shouldRetryPayment = (isLatest: boolean, invoice?: Subscriptions.InvoiceModel): boolean => {
	return isLatest && !!invoice?.external_id && isFailedPayment(invoice);
};

/**
 * Get plan renewal date
 */
const getPlanRenewalDate = (plan?: Subscriptions.IActivePlanModel): string | undefined => {
	// todo check the key, currently its not coming in api def
	const currentBillingPeriodEndDateString = _.get(plan, 'current_billing_period_ending_at');
	const currentBillingPeriodEndDate = currentBillingPeriodEndDateString
		? dateParser(currentBillingPeriodEndDateString, YEAR_MONTH_DATE_FORMAT)
		: undefined;

	const isValid = currentBillingPeriodEndDate && currentBillingPeriodEndDate?.isValid();

	// monthly plan
	if (isValid) {
		const nextBillingDate = currentBillingPeriodEndDate?.add(1, 'day');
		return nextBillingDate?.format(SHORT_DATE_FORMAT);
	}
};

/**
 * Get plan ending date
 */
const getNextPlanDate = (plan?: Subscriptions.IActivePlanModel): string | undefined => {
	const subscriptionDate = plan?.downgrade_plan_date ? dateParser(plan.downgrade_plan_date, YEAR_MONTH_DATE_FORMAT) : undefined;

	if (subscriptionDate?.isValid()) {
		return subscriptionDate.format(SHORT_DATE_FORMAT);
	}
};

const openPlanCancellationMail = (
	plan: Subscriptions.IActivePlanModel,
	supportEmail: string,
	organizationName?: string,
	userName?: string,
): void => {
	if (plan) {
		const subject = `Plan Cancellation - ${plan.plan_name}`;
		const lines = [
			'Hello,',
			' I would like to cancel my current plan with the following details:',
			`Plan Name: ${plan.plan_name}`,
			`Plan Interval: ${plan.interval}`,
			'Please process the cancellation accordingly.',
			'', // empty line
			'Thank you,',
			userName || '[Name]',
			organizationName || '[Practice Name]',
		];

		const cancelPlanUrl = `mailto:${supportEmail}
		?subject=${encodeURIComponent(subject)}
		&body=${encodeURIComponent(_.join(lines, '\n'))}`;

		globalThis.open(cancelPlanUrl);
	}
};

// TODO: revisit this logic; we only have usage based plans & nothing else
const isUsageBasedPlan = (planCode: string): boolean => {
	return _.startsWith(planCode, 'ubp_m');
};

// TODO: revisit because usage ratio is no longer valid, we don't have plan limit
const getUsageRatioMeta = (
	data?: Subscriptions.ICurrentUsage,
): {
	usageRatio: number;
	uniqueAlertBarKey: string;
	usageRatioLabel: string;
	transactionsUsed: number;
	usageBarType: 'info' | 'warning';
	usageDescription: string;
	showPageAlertBar: boolean;
	hasReachedEighty: boolean;
	hasReachedNinety: boolean;
	addOns?: number;
} => {
	const transactionsUsed = _.find(data?.charges_usage, ({ billable_metric }) => billable_metric.code === CHARGES_CODE)?.units || 0;
	const totalTransactions = data?.limits?.default || 0;
	const planCode = data?.plan_code || '';

	const paymentsAddonUsage = _.find(data?.charges_usage, ({ billable_metric }) => billable_metric.code === ADDON_CODES.payments);
	const paymentsUsed = paymentsAddonUsage?.units || 0;
	const paymentsPrice = (paymentsAddonUsage?.amount_cents ?? 0) / 100;

	const addOns = paymentsUsed ? paymentsPrice : undefined;

	if (transactionsUsed && totalTransactions) {
		const usageRatio = _.round((transactionsUsed / totalTransactions) * 100);
		const hasReachedEighty = usageRatio >= 80 && usageRatio < 90;
		const hasReachedNinety = usageRatio >= 90 && usageRatio < 100;
		const hasReachedOrCrossedHundred = usageRatio >= 100;
		const usageRange = hasReachedOrCrossedHundred ? 100 : hasReachedNinety ? 90 : hasReachedEighty ? 80 : 0;
		return {
			// subscriptions section
			transactionsUsed,
			usageBarType: hasReachedOrCrossedHundred ? 'warning' : 'info',
			usageDescription: hasReachedOrCrossedHundred
				? `${transactionsUsed} Transactions (You have exceeded your base transaction allowance.)`
				: `${transactionsUsed} Transactions`,
			// page alert bar
			usageRatio,
			uniqueAlertBarKey: `${data?.from_datetime}---${usageRange}`,
			usageRatioLabel: `${usageRange}%`,
			hasReachedEighty,
			hasReachedNinety,
			showPageAlertBar: !isUsageBasedPlan(planCode) && (hasReachedEighty || hasReachedOrCrossedHundred || hasReachedNinety),
			addOns,
		};
	}

	return {
		// subscriptions section
		transactionsUsed,
		usageBarType: 'info',
		usageDescription: `${transactionsUsed} transactions used`,
		// page alert bar
		usageRatio: 0,
		uniqueAlertBarKey: '',
		usageRatioLabel: '0%',
		hasReachedEighty: false,
		hasReachedNinety: false,
		showPageAlertBar: false,
		addOns,
	};
};

const getPricingSchema = (
	plan: Subscriptions.IActivePlanModel,
): {
	pricingRanges: Subscriptions.IPlanRange[];
	basePrice: number;
	baseTransactionAmount: number;
} => {
	const charges = plan?.currPlanInfo?.charges ?? [];
	const basePrice = (plan?.currPlanInfo?.amount_cents ?? 0) / 100;
	const pricingRanges =
		_.find(charges, ({ billable_metric_code }) => billable_metric_code === CHARGES_CODE)?.properties?.graduated_ranges ?? [];
	const baseTransactionAmount = pricingRanges?.[0]?.to_value ?? 0;
	return { pricingRanges, basePrice, baseTransactionAmount };
};

export {
	isUsageBasedPlan,
	getNextPlanDate,
	renderPlanName,
	getInvoiceAmountBreakdown,
	isFailedPayment,
	shouldRetryPayment,
	getPlanRenewalDate,
	openPlanCancellationMail,
	getUsageRatioMeta,
	getPricingSchema,
};
